import { visionURL } from "../config/environment";
import Jsona from "jsona";
import { useEffect, useState } from "react";
import { useReportPath } from "./utils";
import JsonApiService from "../services/jsonapiService";
import visionCallService from "../services/visionCallService";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  enqueueInfoSnackbar,
  enqueueErrorSnackbar
} from "../redux/actions/snackbarActions";

const dataFormatter = new Jsona();

const assignmentsPreviewApiService = async data =>
  new JsonApiService("assignments_preview", {
    method: "POST",
    body: JSON.stringify({ filter: data }),
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include"
  });

const assignmentsApiService = async () =>
  new JsonApiService("assignments", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include"
  });

const specialAssignmentsApiService = async () =>
  new JsonApiService("special_assignments", {
    baseUrl: `${visionURL}/admin_console/api/administration/assignments`,
    credentials: "include"
  });

const trainingObjectApiService = async () =>
  new JsonApiService("pickable_training_objects", {
    baseUrl: `${visionURL}/admin_console/api/administration/assignments`,
    credentials: "include"
  });

// TODO: needs to be refactored to follow json api spec
export const useEmployeesAssignments = specialAssignmentId => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [
    employeesWithSpecialAssignment,
    setEmployeesWithSpecialAssignment
  ] = useState([]);
  const [employeesWithAssignment, setEmployeesWithAssignment] = useState([]);
  const [
    employeesWithoutSpecialAssignment,
    setEmployeesWithoutSpecialAssignment
  ] = useState([]);
  const [employeesWithExclusion, setEmployeesWithExclusion] = useState([]);
  const [
    employeesWithCompletedSpecialAssignment,
    setEmployeesWithCompletedSpecialAssignment
  ] = useState([]);

  const setEmployees = response => {
    setEmployeesWithSpecialAssignment(response?.assigned);
    setEmployeesWithAssignment(response?.green_assigned);
    setEmployeesWithoutSpecialAssignment(response?.unassigned);
    setEmployeesWithExclusion(response?.excluded);
    setEmployeesWithCompletedSpecialAssignment(response?.completed);
  };

  const fetchAssignments = async () => {
    setIsLoading(true);
    const service = await specialAssignmentsApiService();
    const response = await service
      .related(specialAssignmentId, "employees_assignments")
      .then(response => response.json());
    setIsLoading(false);
    setEmployees(response);
    return response;
  };

  const updateEmployeesAssignments = async (id, data) => {
    setIsUpdating(true);
    const service = await specialAssignmentsApiService();
    const response = await service
      .createRelated(id, "employees_assignments", JSON.stringify(data))
      .then(response => response.json());
    setIsUpdating(false);
    setEmployees(response);
    return response;
  };

  useEffect(() => {
    if (specialAssignmentId) {
      fetchAssignments();
    }
  }, [specialAssignmentId]);

  return {
    employeesWithSpecialAssignment,
    employeesWithAssignment,
    employeesWithoutSpecialAssignment,
    employeesWithExclusion,
    employeesWithCompletedSpecialAssignment,
    updateEmployeesAssignments,
    isLoading,
    isUpdating
  };
};

const bulkAssignmentsApiService = async () =>
  new JsonApiService("bulk_course_assignments", {
    baseUrl: `${visionURL}/admin_console/api/administration/assignments`,
    credentials: "include"
  });

export const useCourses = (referenceType, referenceId) => {
  return {
    getCourses: async ({ page: { number, size }, sortBy, filters }) => {
      const service = await trainingObjectApiService();
      const response = await service
        .page({ number: number, size: size })
        .sort(sortBy)
        .where({ ...filters, referenceId, referenceType })
        .all()
        .then(response => response.json());
      return {
        data: dataFormatter.deserialize(response),
        ...response.meta
      };
    }
  };
};

const useAssignmentNotification = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const notify = (status, bulk) => {
    if (status === 200) {
      const mes = bulk ? "saveBulkSuccess" : "saveSuccess";
      dispatch(
        enqueueInfoSnackbar({
          message: t(`employees.assignments.${mes}`),
          options: {
            autoHideDuration: 20000
          }
        })
      );
    } else {
      dispatch(
        enqueueErrorSnackbar({ message: t("employees.assignments.saveError") })
      );
    }
  };
  return notify;
};

export const useBatchCreateAssignments = () => {
  const [isCreating, setIsCreating] = useState(false);
  const notify = useAssignmentNotification();
  const createAssignments = async (assignmentsData, bulk) => {
    setIsCreating(true);
    const service = await assignmentsApiService();
    const data = assignmentsData.map(({ op, id, ...attributes }) => ({
      op,
      data: {
        type: "assignments",
        ...(!!id && { id }),
        attributes: attributes
      }
    }));
    const response = await service.operations(data, bulk);
    const res = await response.json();
    const operation_data = res["atomic:results"];
    setIsCreating(false);
    let errors = [];
    if (Array.isArray(operation_data)) {
      errors = operation_data.filter(result => "error" in result);
    }
    if (errors.length || (res && res["errors"])) {
      notify(500);
      return;
    } else {
      notify(200, bulk);
      return operation_data;
    }
  };
  return { createAssignments, isCreating };
};

export const useSpecialAssignments = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  return {
    getSpecialAssignments: async ({ page, sortBy, filters }) => {
      setIsFetching(true);
      const service = await specialAssignmentsApiService();
      const response = await service
        .page(page)
        .sort(sortBy)
        .where(filters)
        .include("training_object")
        .all()
        .then(response => response.json());
      setIsFetching(false);
      return {
        data: dataFormatter.deserialize(response),
        ...response.meta
      };
    },
    getSpecialAssignment: async Id => {
      const service = await specialAssignmentsApiService();
      const response = await service
        .include("training_object")
        .find(Id)
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    createNewSpecialAssignment: async data => {
      setIsSubmitting(true);
      const service = await specialAssignmentsApiService();
      const response = await service
        .include("training_object")
        .create(JSON.stringify(data))
        .then(response => response.json());
      const submissionData = dataFormatter.deserialize(response);
      setIsSubmitting(false);
      return submissionData;
    },
    updateSpecialAssignment: async data => {
      setIsSubmitting(true);
      const service = await specialAssignmentsApiService();
      const response = await service
        .update(
          data.id,
          visionCallService.serializeRequestBody(data, "specialAssignment")
        )
        .then(response => response.json());
      const submissionData = dataFormatter.deserialize(response);
      setIsSubmitting(false);
      return submissionData;
    },
    deleteSpecialAssignment: async specialAssignmentId => {
      const service = await specialAssignmentsApiService();
      const response = await service.delete(specialAssignmentId);
      return response;
    },
    isSubmitting,
    isFetching
  };
};

export const useBulkCourseAssignments = () => {
  return {
    getBulkCourseAssignments: async ({ page, sortBy, filters }) => {
      const service = await bulkAssignmentsApiService();
      const response = await service
        .page(page)
        .sort(sortBy)
        .where(filters)
        .all()
        .then(response => response.json());
      return {
        data: dataFormatter.deserialize(response),
        totalCount: response.meta ? response.meta.totalCount : 0
      };
    }
  };
};

export const useAssignments = () => {
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAssignments = async filters => {
    setLoading(true);
    const service = await assignmentsPreviewApiService(filters);
    const response = await service
      .include("training_object")
      .all()
      .then(response => response.json());

    setLoading(false);
    const assignments = dataFormatter.deserialize(response);
    return assignments;
  };

  const createAssignment = async newAssignmentData => {
    setLoading(true);
    const service = await assignmentsApiService();
    const response = await service
      .create(
        visionCallService.serializeRequestBody(
          {
            ...newAssignmentData,
            relationshipNames: ["trainingObject", "reference"]
          },
          "assignment",
          ["trainingObject", "reference"]
        )
      )
      .then(response => response.json());

    setLoading(false);
    const newAssignment = dataFormatter.deserialize(response);
    return newAssignment;
  };

  const updateAssignment = async newAssignmentData => {
    setLoading(true);
    const service = await assignmentsApiService();
    const response = await service
      .update(
        newAssignmentData.id,
        visionCallService.serializeRequestBody(
          {
            ...newAssignmentData,
            relationshipNames: ["trainingObject", "reference"]
          },
          "assignment",
          ["trainingObject", "reference"]
        )
      )
      .then(response => response.json());

    setLoading(false);
    const newAssignment = dataFormatter.deserialize(response);
    return newAssignment;
  };

  const deleteAssignment = async assignmentId => {
    setIsDeleting(true);
    const service = await assignmentsApiService();
    await service.delete(assignmentId);
    setIsDeleting(false);
    return assignmentId;
  };

  return {
    fetchAssignments,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    isDeleting,
    loading
  };
};

export const useSpecialAssignmentsReport = () => {
  const reportUrl = `${visionURL}/admin_console/api/administration/assignments/special_assignments_report/generate`;
  return useReportPath(reportUrl);
};
